import { experienceUrl } from '@/lib/urls';
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Wine, Music, MapPin, Calendar, Clock, Star, ArrowRight, UtensilsCrossed, Gift, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from '@/contexts/LocationContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';

const DateNightHub = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isGold } = useAuth();
  const { currentNeighborhood } = useLocation();
  const [dateNightExperiences, setDateNightExperiences] = useState([]);
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  const neighborhoodName = currentNeighborhood?.name || 'Denver';
  const neighborhoodSlug = currentNeighborhood?.slug || currentNeighborhood?.id || '';

  useEffect(() => {
    fetchData();
  }, [currentNeighborhood]);

  const fetchData = async () => {
    try {
      const [dateNightRes, eventsRes, venuesRes] = await Promise.all([
        api.get(`/vouchers/date-night${neighborhoodSlug ? `?neighborhood=${neighborhoodSlug}` : ''}`),
        api.get('/events', { params: { category: 'date_night' } }),
        api.get('/venues')
      ]);
      setDateNightExperiences(dateNightRes.data.experiences || []);
      setEvents(eventsRes.data);
      setVenues(venuesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Curated date night packages
  const packages = [
    {
      id: 'romantic-evening',
      title: 'The Romantic Evening',
      icon: Heart,
      description: 'Perfect for couples seeking an intimate, memorable night',
      theme: 'from-rose-500 to-pink-500',
      itinerary: [
        { time: '6:00 PM', activity: 'Cocktails at Noble Riot', icon: Wine },
        { time: '7:30 PM', activity: 'Dinner for Two', icon: UtensilsCrossed },
        { time: '9:00 PM', activity: 'Live Jazz at The Loft', icon: Music }
      ],
      price: 185.00,
      gold_price: 155.00,
      includes: [
        'Reserved table for two',
        'Welcome cocktails',
        '3-course dinner',
        'Premium concert seating'
      ]
    },
    {
      id: 'adventure-night',
      title: 'The Adventure Night',
      icon: Sparkles,
      description: 'For adventurous couples who love to explore',
      theme: 'from-indigo-500 to-purple-500',
      itinerary: [
        { time: '5:30 PM', activity: `${neighborhoodName} Art Walk`, icon: MapPin },
        { time: '7:00 PM', activity: 'Craft Beer Tasting', icon: Wine },
        { time: '9:00 PM', activity: 'Live Music Show', icon: Music }
      ],
      price: 145.00,
      gold_price: 120.00,
      includes: [
        'Guided art district tour',
        'Craft beer flight for two',
        'Concert tickets',
        'Souvenir photo'
      ]
    },
    {
      id: 'gold-exclusive',
      title: 'The Gold Experience',
      icon: Star,
      description: 'Our most exclusive date night package',
      theme: 'from-amber-500 to-yellow-500',
      gold_exclusive: true,
      itinerary: [
        { time: '6:00 PM', activity: 'Private Cocktail Tasting', icon: Wine },
        { time: '7:30 PM', activity: 'Chef\'s Table Dinner', icon: UtensilsCrossed },
        { time: '9:30 PM', activity: 'VIP Concert Access', icon: Music },
        { time: '11:00 PM', activity: 'Private After-Party', icon: Sparkles }
      ],
      price: 350.00,
      gold_price: 285.00,
      includes: [
        'Private mixologist session',
        'Multi-course tasting menu',
        'VIP seating',
        'Champagne & dessert',
        'Professional photos'
      ]
    }
  ];

  return (
    <ConsumerLayout darkTheme>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-rose-900/40 via-pink-900/30 to-[#0f172a] py-16 sm:py-20" data-testid="date-night-hero">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.4),transparent)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Heart className="w-16 h-16 text-rose-400 mx-auto mb-6 fill-current" />
            <h1 className="headline-serif text-4xl lg:text-6xl font-bold text-slate-50 mb-6">
              Design Your Perfect
              <br />
              <span className="bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text text-transparent italic">
                Date Night
              </span>
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Pre-curated romantic experiences and itineraries in {neighborhoodName}. From cocktails to concerts, we've designed the perfect evening.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        
        {/* Date Night Experiences from Partners */}
        {dateNightExperiences.length > 0 && (
          <div className="mb-16">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <div>
                <h2 className="headline-serif text-2xl sm:text-3xl font-bold text-slate-50 mb-2">
                  Date Night Experiences
                </h2>
                <p className="text-slate-400">
                  Book romantic experiences from {neighborhoodName} partner venues
                </p>
              </div>
              <Button
                variant="outline"
                className="border-rose-500 text-rose-400 hover:bg-rose-500/10"
                onClick={() => navigate('/marketplace')}
              >
                View All Experiences
              </Button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {dateNightExperiences.map((exp) => {
                const displayPrice = isGold && exp.gold_price ? exp.gold_price : exp.price;
                const hasSavings = isGold && exp.gold_price && exp.gold_price < exp.price;

                return (
                  <motion.div
                    key={exp.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -4 }}
                    data-testid={`date-night-exp-${exp.id}`}
                  >
                    <Link to={experienceUrl(exp)}>
                      <Card className="overflow-hidden bg-slate-800 border-rose-500/30 hover:border-rose-500/60 transition-all cursor-pointer h-full group">
                        <div className="aspect-[4/3] bg-gradient-to-br from-rose-900/50 to-pink-900/50 flex items-center justify-center relative overflow-hidden">
                          {exp.images && exp.images[0] ? (
                            <img
                              src={exp.images[0]}
                              alt={exp.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <Heart className="w-12 h-12 text-rose-400/50" />
                          )}
                          {hasSavings && (
                            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-amber-400 text-black px-2 py-1 rounded text-xs font-bold">
                              SAVE ${(exp.price - exp.gold_price).toFixed(0)}
                            </div>
                          )}
                          <Badge className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-rose-500 text-white border-0 text-xs">
                            DATE NIGHT
                          </Badge>
                        </div>
                        <CardContent className="p-4 bg-slate-800">
                          <p className="text-xs text-rose-400 font-medium mb-1 line-clamp-1">{exp.venue_name}</p>
                          <h3 className="font-bold text-slate-50 mb-2 text-sm sm:text-base line-clamp-2 group-hover:text-rose-400 transition-colors">
                            {exp.title}
                          </h3>
                          <div className="flex items-center justify-between">
                            <div>
                              {hasSavings && (
                                <span className="text-sm text-slate-500 line-through mr-2">${exp.price}</span>
                              )}
                              <span className="text-lg font-bold text-slate-50">${displayPrice}</span>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-rose-400 transition-colors" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Curated Packages */}
        <div className="mb-16">
          <h2 className="headline-serif text-2xl sm:text-3xl font-bold text-slate-50 mb-2">Curated Date Packages</h2>
          <p className="text-slate-400 mb-8">Complete evening itineraries designed for romance</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {packages.map((pkg, index) => {
              const Icon = pkg.icon;
              const displayPrice = isGold ? pkg.gold_price : pkg.price;
              const savings = pkg.price - pkg.gold_price;

              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`package-${pkg.id}`}
                >
                  <Card className={`h-full border-slate-700 overflow-hidden bg-slate-800 ${pkg.gold_exclusive && !isGold ? 'opacity-75' : ''}`}>
                    <div className={`h-2 bg-gradient-to-r ${pkg.theme}`} />
                    
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pkg.theme} flex items-center justify-center`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        {pkg.gold_exclusive && (
                          <div className="bg-amber-400/20 border border-amber-400 px-3 py-1 rounded-full">
                            <span className="text-amber-400 font-bold text-xs flex items-center gap-1">
                              <Star className="w-3 h-3 fill-current" />
                              GOLD ONLY
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <CardTitle className="text-xl sm:text-2xl text-slate-50 mb-2">{pkg.title}</CardTitle>
                      <p className="text-sm text-slate-400">{pkg.description}</p>
                    </CardHeader>

                    <CardContent className="space-y-6 pt-0">
                      {/* Itinerary */}
                      <div>
                        <h4 className="font-bold text-sm text-slate-500 mb-3">Your Evening</h4>
                        <div className="space-y-3">
                          {pkg.itinerary.map((item, idx) => {
                            const ItemIcon = item.icon;
                            return (
                              <div key={idx} className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center">
                                  <ItemIcon className="w-4 h-4 text-rose-400" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-200">{item.activity}</p>
                                  <p className="text-xs text-slate-500">{item.time}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Includes */}
                      <div>
                        <h4 className="font-bold text-sm text-slate-500 mb-3">What's Included</h4>
                        <ul className="space-y-2">
                          {pkg.includes.map((item, idx) => (
                            <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                              <span className="text-emerald-400 mt-0.5">✓</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Pricing */}
                      <div className="pt-4 border-t border-slate-700">
                        <div className="flex items-baseline gap-2 mb-4">
                          <span className="text-3xl font-bold text-slate-50">
                            ${displayPrice.toFixed(0)}
                          </span>
                          {isGold && (
                            <>
                              <span className="text-lg text-slate-500 line-through">
                                ${pkg.price.toFixed(0)}
                              </span>
                              <span className="text-sm text-emerald-400 font-medium">
                                Save ${savings.toFixed(0)}
                              </span>
                            </>
                          )}
                          <span className="text-sm text-slate-500">/couple</span>
                        </div>

                        {pkg.gold_exclusive && !isGold ? (
                          <Button
                            className="w-full bg-amber-400 hover:bg-amber-500 text-black font-semibold"
                            onClick={() => navigate('/gold-club')}
                            data-testid={`upgrade-for-${pkg.id}`}
                          >
                            <Star className="w-4 h-4 mr-2 fill-current" />
                            Join Gold to Book
                          </Button>
                        ) : (
                          <Button
                            className="w-full bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={() => {
                              if (!isAuthenticated) {
                                navigate('/auth');
                              } else {
                                navigate(`/checkout/package/${pkg.id}`);
                              }
                            }}
                            data-testid={`book-${pkg.id}`}
                          >
                            Book This Package
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Individual Date Night Events */}
        {events.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="headline-serif text-2xl sm:text-3xl font-bold text-slate-50 mb-2">Build Your Own</h2>
                <p className="text-slate-400">
                  Mix and match individual date night events
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.slice(0, 6).map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="group"
                  data-testid={`date-event-${event.id}`}
                >
                  <Card className="overflow-hidden border-slate-700 bg-slate-800 hover:border-rose-500/50 transition-all">
                    <div className="aspect-[16/9] bg-gradient-to-br from-rose-900/30 to-pink-900/30 flex items-center justify-center relative overflow-hidden">
                      {event.images && event.images[0] ? (
                        <img
                          src={event.images[0]}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <Calendar className="w-16 h-16 text-rose-400/50" />
                      )}
                    </div>
                    <CardContent className="p-6 bg-slate-800">
                      <h3 className="text-xl font-bold text-slate-50 mb-2 group-hover:text-rose-400 transition-colors line-clamp-2">
                        {event.title}
                      </h3>
                      <p className="text-sm text-slate-400 mb-4">
                        {event.venue_name}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <Calendar className="w-4 h-4" />
                          {event.date}
                        </div>
                        <div className="text-lg font-bold text-slate-50">
                          ${event.price.toFixed(2)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {dateNightExperiences.length === 0 && events.length === 0 && !loading && (
          <div className="text-center py-16 bg-slate-800/50 rounded-2xl border border-slate-700 mb-16">
            <Heart className="w-16 h-16 text-rose-400/40 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-300 mb-2">Date Night Experiences Coming to {neighborhoodName}</h3>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              We're partnering with romantic venues in your area. Check back soon or browse all experiences!
            </p>
            <Button
              variant="outline"
              className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
              onClick={() => navigate('/marketplace')}
            >
              Browse All Experiences
            </Button>
          </div>
        )}

        {/* CTA Section */}
        {!isGold && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-gradient-to-r from-amber-900/50 to-amber-800/50 border border-amber-600/30 text-center"
          >
            <Star className="w-12 h-12 text-amber-400 mx-auto mb-4 fill-current" />
            <h2 className="headline-serif text-2xl sm:text-3xl font-bold text-slate-50 mb-4">Unlock Exclusive Date Packages</h2>
            <p className="text-lg text-amber-200/80 mb-6 max-w-2xl mx-auto">
              Gold members save up to $65 per package and get access to our most exclusive experiences.
            </p>
            <Button
              size="lg"
              className="bg-amber-400 hover:bg-amber-500 text-black font-bold"
              onClick={() => navigate('/gold-club')}
              data-testid="date-night-gold-cta"
            >
              Join Gold Club
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        )}
      </div>

    </ConsumerLayout>
  );
};

export default DateNightHub;
